import type { CreateBugReportPayload } from '../types/bugReports';
import type { UniversalLogEntry } from '../types/logging';

export type AutomaticBugReportDeviceKind = 'phone' | 'tablet' | 'desktop';

export const AUTOMATIC_BUG_REPORT_THROTTLE_MS = 15 * 60 * 1000;
const AUTOMATIC_CAPTURE_MAX_TEXT_LENGTH = 400;
const AUTOMATIC_CAPTURE_MAX_STACK_LENGTH = 4000;
const AUTOMATIC_CAPTURE_MAX_OBJECT_KEYS = 12;
const AUTOMATIC_CAPTURE_MAX_ARRAY_ITEMS = 8;
const AUTOMATIC_CAPTURE_MAX_DEPTH = 4;

const AUTOMATIC_BUG_REPORT_CATEGORIES = new Set([
  'react/error-boundary',
  'window/error',
  'window/unhandledrejection',
]);

const IGNORED_ERROR_PATTERNS = [
  /signal is aborted without reason/i,
  /\bAbortError\b/i,
  /Loading chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

const SENSITIVE_DETAIL_KEY_PATTERNS = ['password', 'secret', 'token', 'auth', 'cookie', 'email', 'phone'];

function normalizeSegment(value: string | undefined, maxLength = 120): string {
  const trimmed = (value || '').trim().replace(/\s+/g, ' ');
  if (!trimmed) {
    return '';
  }
  return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength)}...` : trimmed;
}

function isSensitiveDetailKey(key: string): boolean {
  const lowered = key.toLowerCase();
  return SENSITIVE_DETAIL_KEY_PATTERNS.some((pattern) => lowered.includes(pattern));
}

function sanitizeAutomaticCaptureValue(value: unknown, depth = 0): unknown {
  if (depth >= AUTOMATIC_CAPTURE_MAX_DEPTH) {
    return '[truncated]';
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    return normalizeSegment(value, AUTOMATIC_CAPTURE_MAX_TEXT_LENGTH);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    const items = value
      .slice(0, AUTOMATIC_CAPTURE_MAX_ARRAY_ITEMS)
      .map((item) => sanitizeAutomaticCaptureValue(item, depth + 1));
    if (value.length > AUTOMATIC_CAPTURE_MAX_ARRAY_ITEMS) {
      items.push('[truncated]');
    }
    return items;
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    const result: Record<string, unknown> = {};

    for (const [index, [key, nestedValue]] of entries.entries()) {
      if (index >= AUTOMATIC_CAPTURE_MAX_OBJECT_KEYS) {
        result.__truncated__ = true;
        break;
      }
      if (isSensitiveDetailKey(key)) {
        result[key] = '[REDACTED]';
        continue;
      }
      result[key] = sanitizeAutomaticCaptureValue(nestedValue, depth + 1);
    }

    return result;
  }

  return normalizeSegment(String(value), AUTOMATIC_CAPTURE_MAX_TEXT_LENGTH);
}

export function buildAutomaticBugReportFingerprint(entry: UniversalLogEntry): string {
  const category = normalizeSegment(entry.category, 80).toLowerCase();
  const page = normalizeSegment(entry.page, 120).toLowerCase();
  const message = normalizeSegment(entry.message, 180).toLowerCase();
  return `${category}::${page}::${message}`;
}

export function buildAutomaticCaptureExcerpt(entry: UniversalLogEntry): Record<string, unknown> {
  const excerpt: Record<string, unknown> = {
    category: normalizeSegment(entry.category, 80) || 'unknown',
    message: normalizeSegment(entry.message, 240) || 'Unknown client error',
    page: normalizeSegment(entry.page, 200) || undefined,
    captured_at_utc: new Date(entry.timestamp).toISOString(),
  };

  if (entry.stackTrace) {
    excerpt.stackTrace = normalizeSegment(entry.stackTrace, AUTOMATIC_CAPTURE_MAX_STACK_LENGTH);
  }

  if (entry.details !== undefined) {
    excerpt.details = sanitizeAutomaticCaptureValue(entry.details);
  }

  return excerpt;
}

export function isIgnoredAutomaticBugMessage(message: string | undefined, stackTrace?: string): boolean {
  const haystack = `${message || ''}\n${stackTrace || ''}`;
  return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(haystack));
}

export function shouldAutoReportLogEntry(
  entry: UniversalLogEntry,
  params: { deviceKind: AutomaticBugReportDeviceKind }
): boolean {
  if (params.deviceKind !== 'phone') {
    return false;
  }
  if (entry.level !== 'error') {
    return false;
  }
  if (!AUTOMATIC_BUG_REPORT_CATEGORIES.has(entry.category)) {
    return false;
  }
  if (isIgnoredAutomaticBugMessage(entry.message, entry.stackTrace)) {
    return false;
  }
  return true;
}

export function shouldThrottleAutomaticBugReport(
  storage: Pick<Storage, 'getItem'> | undefined,
  fingerprint: string,
  now: number,
  cooldownMs: number = AUTOMATIC_BUG_REPORT_THROTTLE_MS
): boolean {
  if (!storage) {
    return false;
  }

  try {
    const raw = storage.getItem(fingerprint);
    if (!raw) {
      return false;
    }
    const lastSubmittedAt = Number(raw);
    if (!Number.isFinite(lastSubmittedAt)) {
      return false;
    }
    return now - lastSubmittedAt < cooldownMs;
  } catch {
    return false;
  }
}

export function markAutomaticBugReportSubmitted(
  storage: Pick<Storage, 'setItem'> | undefined,
  fingerprint: string,
  now: number
): void {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(fingerprint, String(now));
  } catch {
    // Ignore storage failures.
  }
}

export function buildAutomaticBugReportPayload(params: {
  entry: UniversalLogEntry;
  context: Record<string, unknown>;
  reporterName?: string | null;
}): CreateBugReportPayload {
  const { entry, context, reporterName } = params;
  const route = entry.page || 'unknown route';
  const category = normalizeSegment(entry.category, 80) || 'frontend error';
  const summary = normalizeSegment(entry.message, 72) || 'Unhandled mobile client error';

  return {
    title: `Auto mobile error: ${summary}`,
    description: `Automatically captured mobile client error on ${route}. Category: ${category}.`,
    steps_to_reproduce: 'Automatically captured from a phone client error. Review captured context and recent logs.',
    expected_behavior: 'The mobile page should continue running without an uncaught client error.',
    actual_behavior: normalizeSegment(entry.message, 240) || 'An uncaught mobile client error was logged.',
    frequency: 'unknown',
    affected_record: {
      route,
    },
    severity: 'high',
    reporter_name: reporterName || 'Automatic mobile capture',
    context,
  };
}
